'use client';

import { ConfigCatProvider, createFlagOverridesFromMap, OverrideBehaviour } from 'configcat-react';
import { ReactNode } from 'react';

import { HERMETIC_FEATURE_FLAGS } from '~/definitions/hermetic-feature-flags';

const EVERY_15_MINUTES = 900;

// Hermetic E2E: when the SDK key is the sentinel below, feature flags are served
// from local overrides (no ConfigCat CDN call) so the Docker Compose stack stays
// fully offline and flag values are deterministic. The values live in
// `~/definitions/hermetic-feature-flags` because the MSW integration harness serves
// the same map as its baseline and the two must not drift.
// This branch is inert in every real environment (real keys never match).
const HERMETIC_CONFIG_CAT_KEY = 'hermetic-local-overrides';

interface FeatureFlagProviderProps {
  readonly children: ReactNode;
  readonly sdkKey: string;
}

/**
 * Client-side wrapper around ConfigCatProvider. Kept as a Client Component so the
 * client-only `createFlagOverridesFromMap` helper can be used for hermetic E2E runs.
 */
export const FeatureFlagProvider = ({ children, sdkKey }: FeatureFlagProviderProps) => {
  const isHermetic = sdkKey === HERMETIC_CONFIG_CAT_KEY;
  const options = {
    pollIntervalSeconds: EVERY_15_MINUTES,
    ...(isHermetic
      ? { flagOverrides: createFlagOverridesFromMap(HERMETIC_FEATURE_FLAGS, OverrideBehaviour.LocalOnly) }
      : {}),
  };

  return (
    <ConfigCatProvider
      sdkKey={sdkKey}
      options={options}
    >
      {children}
    </ConfigCatProvider>
  );
};
